import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0f4f8",
          200: "#a2e9f1",
          300: "#73dee9",
          400: "#4caf50", // Solid FIG Green Shade
          500: "#66db76", // FIG Green
          600: "#52af5e",
          700: "#3d8346",
          800: "#29582f",
          900: "#142c17",
          gradient: "linear-gradient(90deg, #66db76, #ffffff)", // Green to White Gradient
        },
        greenAccent: {
          100: "#e0f8e4",
          200: "#c2f1c9",
          300: "#a3e9ad",
          400: "#85e292",
          500: "#66db76",
          600: "#52af5e",
          700: "#3d8346",
          800: "#29582f",
          900: "#142c17",
        },
        background: {
          default: "#141414",
          gradient: "linear-gradient(180deg, #66db76, #ffffff)", // Green to White Gradient
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#d0f4f8",
          200: "#a2e9f1",
          300: "#73dee9",
          400: "#4caf50", // Solid FIG Green Shade
          500: "#66db76", // FIG Green
          600: "#52af5e",
          700: "#3d8346",
          800: "#29582f",
          900: "#142c17",
          gradient: "linear-gradient(90deg, #66db76, #ffffff)", // Green to White Gradient
        },
        greenAccent: {
          100: "#e0f8e4",
          200: "#c2f1c9",
          300: "#a3e9ad",
          400: "#85e292",
          500: "#66db76",
          600: "#52af5e",
          700: "#3d8346",
          800: "#29582f",
          900: "#142c17",
        },
        background: {
          default: "#fcfcfc",
          gradient: "linear-gradient(180deg, #66db76, #ffffff)", // Green to White Gradient
        },
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.background.default,
            },
          }
        : {
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.background.default,
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
